import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, Plus, Trash, Download, Clock, CheckCircle, XCircle, Warning, PaperPlaneTilt, LinkSimple, ArrowClockwise } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_SIGNATAIRE = {
  en_attente: { label: 'En attente', color: 'bg-[#F5F5F3] text-[#6B6B6B] border-[#E0E0DB]', icon: Clock },
  code_envoye: { label: 'Code envoyé', color: 'bg-[#E3F2FD] text-[#1E3A5F] border-[#1E3A5F]', icon: PaperPlaneTilt },
  signe: { label: 'Signé', color: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]', icon: CheckCircle },
  refuse: { label: 'Refusé', color: 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]', icon: XCircle },
};

const STATUT_SIG = {
  en_cours: { label: 'En cours', color: 'bg-[#FFF8E1] text-[#B8860B] border-[#B8860B]' },
  complete: { label: 'Complète', color: 'bg-[#E8F5E9] text-[#2D6A4F] border-[#2D6A4F]' },
  annule: { label: 'Annulée', color: 'bg-[#F5F5F3] text-[#6B6B6B] border-[#E0E0DB]' },
  expire: { label: 'Expirée', color: 'bg-[#FFEBEE] text-[#8B3A3A] border-[#8B3A3A]' },
};

export default function SignatureManager({ chantierId, documentType, documentId, documentTitle, defaultSignataires = [] }) {
  const { user } = useAuth();
  const canRequest = user?.role === 'admin' || user?.role === 'architect';
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/signatures`, {
        params: { document_type: documentType, document_id: documentId },
        withCredentials: true,
      });
      setSignatures(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [chantierId, documentType, documentId]);

  useEffect(() => { if (documentId) load(); }, [load, documentId]);

  const handleCancel = async (sigId) => {
    if (!confirm('Annuler cette demande de signature ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/signatures/${sigId}`, { withCredentials: true });
      toast.success('Demande annulée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadCert = async (sigId) => {
    try {
      const res = await axios.get(`${API_URL}/api/signatures/${sigId}/certificat`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `certificat_${sigId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  const activeSig = signatures.find((s) => s.statut === 'en_cours') || signatures[0];

  return (
    <div className="border border-[#E0E0DB] bg-[#FAFAF8] p-4" data-testid="signature-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="bold" className="text-[#C4773B]" />
          <h4 className="text-sm font-medium text-[#2C2C2C]">Signatures électroniques</h4>
          {signatures.length > 0 && <Badge className="rounded-none bg-[#F5F5F3] text-[#6B6B6B] border border-[#E0E0DB]">{signatures.length}</Badge>}
        </div>
        {canRequest && !activeSig && (
          <Button size="sm" onClick={() => setShowRequest(true)} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-1" data-testid="request-signatures-btn">
            <Plus size={14} weight="bold" /> Demander les signatures
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-[#6B6B6B]">Chargement...</p>
      ) : signatures.length === 0 ? (
        <p className="text-xs text-[#6B6B6B] italic">Aucune demande de signature pour ce document. Cliquez sur "Demander les signatures" pour initier le processus avec vérification SMS.</p>
      ) : (
        <div className="space-y-3">
          {signatures.map((sig) => (
            <SignatureCard key={sig.id} sig={sig} chantierId={chantierId} canManage={canRequest} onChanged={load} onCancel={() => handleCancel(sig.id)} onDownloadCert={() => handleDownloadCert(sig.id)} />
          ))}
        </div>
      )}

      {showRequest && (
        <RequestSignatureDialog
          chantierId={chantierId}
          documentType={documentType}
          documentId={documentId}
          documentTitle={documentTitle}
          defaultSignataires={defaultSignataires}
          onClose={() => setShowRequest(false)}
          onCreated={() => { setShowRequest(false); load(); }}
        />
      )}
    </div>
  );
}

// Fluidité B4 (P5#13) — statut RÉEL de l'e-mail d'invitation (plus de « Email envoyé »
// affiché sur un échec) ; lien de signature à copier ; relance du signataire (P5#14).
const texteInvitation = (s) => {
  const st = s.invitation?.statut;
  if (!st) return 'En attente';
  if (st === 'envoye') return `E-mail envoyé${s.nb_relances ? ` · relancé ${s.nb_relances} fois` : ''}`;
  if (st === 'en_file_mode_preparation') return "E-mail en attente d'envoi (mode préparation)";
  if (st === 'notification_desactivee') return "E-mail non envoyé (e-mails automatiques désactivés)";
  return "L'e-mail n'est pas parti : copiez le lien de signature";
};

function SignatureCard({ sig, chantierId, canManage, onChanged, onCancel, onDownloadCert }) {
  const copierLien = async (s) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/signatures/${sig.id}/signataires/${s.id}/lien`, { withCredentials: true });
      const url = data.url && data.url.startsWith('http') ? data.url : `${window.location.origin}${data.chemin}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Lien de signature de ${s.nom} copié`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copie impossible');
    }
  };
  const relancer = async (s) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/signatures/${sig.id}/signataires/${s.id}/relancer`, {}, { withCredentials: true });
      if (data.ok) toast.success(data.message); else toast.warning(data.message);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    }
  };

  const stConfig = STATUT_SIG[sig.statut] || STATUT_SIG.en_cours;
  const nbSignes = sig.signataires.filter((s) => s.statut === 'signe').length;
  const nbRefuses = sig.signataires.filter((s) => s.statut === 'refuse').length;
  const expireDate = sig.expire_le ? new Date(sig.expire_le) : null;
  const expired = expireDate && expireDate < new Date();

  return (
    <div className="bg-white border border-[#E0E0DB] p-3">
      <div className="flex items-start justify-between mb-2 gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`rounded-none border text-xs ${stConfig.color}`}>{stConfig.label}</Badge>
            <span className="text-xs text-[#6B6B6B]">Demandée le {new Date(sig.demande_le).toLocaleDateString('fr-FR')}</span>
            {expired && sig.statut === 'en_cours' && <Badge className="rounded-none bg-[#FFEBEE] text-[#8B3A3A] border border-[#8B3A3A] text-xs">Expirée</Badge>}
          </div>
          <p className="text-xs text-[#6B6B6B]">
            {nbSignes} signée{nbSignes > 1 ? 's' : ''} / {sig.signataires.length}
            {nbRefuses > 0 && ` · ${nbRefuses} refus`}
          </p>
        </div>
        <div className="flex gap-1">
          {(sig.statut === 'complete' || nbSignes > 0) && (
            <Button size="sm" variant="outline" className="rounded-none gap-1 h-7 text-xs" onClick={onDownloadCert} data-testid={`download-cert-${sig.id}`}>
              <Download size={12} /> Certificat
            </Button>
          )}
          {canManage && sig.statut === 'en_cours' && nbSignes === 0 && (
            <Button size="sm" variant="outline" className="rounded-none gap-1 h-7 text-xs text-[#8B3A3A]" onClick={onCancel} data-testid={`cancel-sig-${sig.id}`}>
              <Trash size={12} /> Annuler
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        {sig.signataires.map((s) => {
          const cfg = STATUT_SIGNATAIRE[s.statut] || STATUT_SIGNATAIRE.en_attente;
          const Icon = cfg.icon;
          return (
            <div key={s.id} className="flex items-center justify-between text-xs py-1.5 px-2 bg-[#FAFAF8] border border-[#F0F0EB]">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Icon size={14} className={s.statut === 'signe' ? 'text-[#2D6A4F]' : s.statut === 'refuse' ? 'text-[#8B3A3A]' : 'text-[#6B6B6B]'} weight={s.statut === 'signe' ? 'fill' : 'regular'} />
                <div className="min-w-0">
                  <div className="font-medium text-[#2C2C2C] truncate">{s.nom}{s.role && <span className="text-[#6B6B6B] font-normal"> — {s.role}</span>}</div>
                  <div className="text-[10px] text-[#6B6B6B]">
                    {s.statut === 'signe' && s.signe_le && `Signé le ${new Date(s.signe_le).toLocaleString('fr-FR')}`}
                    {s.statut === 'refuse' && <>Refusé · {s.refus_motif || 'Motif non précisé'}</>}
                    {s.statut === 'en_attente' && (
                      <span className={s.invitation?.statut && s.invitation.statut !== 'envoye' ? 'text-[#8B3A3A]' : ''} data-testid={`invitation-${s.id}`}>
                        {texteInvitation(s)}
                      </span>
                    )}
                    {s.statut === 'code_envoye' && (s.code_canal === 'email' ? 'Code envoyé par e-mail (SMS indisponible)' : 'Code SMS envoyé')}
                  </div>
                </div>
              </div>
              {canManage && sig.statut === 'en_cours' && !['signe', 'refuse'].includes(s.statut) && (
                <div className="flex gap-1 ml-2">
                  <button type="button" onClick={() => copierLien(s)} className="text-[10px] underline text-[#1E3A5F] inline-flex items-center gap-0.5" data-testid={`copier-lien-${s.id}`}>
                    <LinkSimple size={11} /> Copier le lien
                  </button>
                  <button type="button" onClick={() => relancer(s)} className="text-[10px] underline text-[#1E3A5F] inline-flex items-center gap-0.5" data-testid={`relancer-${s.id}`}>
                    <ArrowClockwise size={11} /> Relancer
                  </button>
                </div>
              )}
              <Badge className={`rounded-none border text-[10px] px-1 py-0 ${cfg.color} ml-2`}>{cfg.label}</Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RequestSignatureDialog({ chantierId, documentType, documentId, documentTitle, defaultSignataires, onClose, onCreated }) {
  const [titre, setTitre] = useState(documentTitle || '');
  const [signataires, setSignataires] = useState(
    defaultSignataires.length > 0
      ? defaultSignataires.map((s) => ({ ...s, obligatoire: true }))
      : [{ nom: '', email: '', telephone: '', role: '', obligatoire: true }],
  );
  const [saving, setSaving] = useState(false);

  const updateSig = (i, patch) => {
    const next = [...signataires];
    next[i] = { ...next[i], ...patch };
    setSignataires(next);
  };

  const addSig = () => setSignataires([...signataires, { nom: '', email: '', telephone: '', role: '', obligatoire: true }]);
  const removeSig = (i) => setSignataires(signataires.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    const clean = signataires.filter((s) => s.nom && s.email && s.telephone);
    if (clean.length === 0) return toast.error('Au moins un signataire avec nom, email et téléphone');
    try {
      setSaving(true);
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/signatures`, {
        document_type: documentType,
        document_id: documentId,
        titre: titre || undefined,
        signataires: clean,
      }, { withCredentials: true });
      // Statut réel des e-mails (P5#13)
      const echecs = (data?.signataires || []).filter((x) => x.invitation && x.invitation.statut !== 'envoye');
      if (echecs.length === 0) toast.success(`${clean.length} invitation(s) envoyée(s) par e-mail`);
      else toast.warning(`${echecs.length} invitation(s) non partie(s) : utilisez « Copier le lien » pour l'envoyer autrement.`, { duration: 8000 });
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-signature-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Demander les signatures électroniques</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Intitulé du document</Label>
            <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder={documentTitle} className="rounded-none mt-1" data-testid="input-titre" />
          </div>

          <div className="bg-[#FFF5EB] border border-[#F0CDA6] p-3 text-xs text-[#6B4A25]">
            <ShieldCheck size={14} weight="bold" className="inline mr-1" />
            Chaque signataire recevra un e-mail avec un lien unique. Il devra saisir un code SMS à 6 chiffres envoyé à son numéro pour signer
            (par e-mail si le SMS ne part pas).
          </div>
          {signataires.some((s) => !(s.nom && s.email && s.telephone)) && (
            <p className="text-xs text-[#8B3A3A]" data-testid="signataires-incomplets">
              Les signataires sans nom, e-mail ou téléphone ne seront pas invités : complétez-les, retirez-les, ou faites signer le PV sur papier.
            </p>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Signataires ({signataires.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={addSig} className="rounded-none gap-1 h-7" data-testid="add-signataire-btn">
                <Plus size={12} /> Ajouter
              </Button>
            </div>
            <div className="space-y-3">
              {signataires.map((s, i) => (
                <div key={i} className="border border-[#E0E0DB] p-3 bg-white" data-testid={`signataire-row-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-[#6B6B6B]">Nom complet</Label>
                      <Input value={s.nom} onChange={(e) => updateSig(i, { nom: e.target.value })} className="rounded-none h-8" required />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#6B6B6B]">Rôle</Label>
                      <Input value={s.role} onChange={(e) => updateSig(i, { role: e.target.value })} className="rounded-none h-8" placeholder="Maître d'ouvrage, Entreprise lot 01..." />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#6B6B6B]">Email</Label>
                      <Input type="email" value={s.email} onChange={(e) => updateSig(i, { email: e.target.value })} className="rounded-none h-8" required />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#6B6B6B]">Téléphone mobile (format int.)</Label>
                      <Input type="tel" value={s.telephone} onChange={(e) => updateSig(i, { telephone: e.target.value })} className="rounded-none h-8" placeholder="+33612345678" required />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center gap-2 text-xs text-[#6B6B6B]">
                      <input type="checkbox" checked={s.obligatoire} onChange={(e) => updateSig(i, { obligatoire: e.target.checked })} />
                      Signature obligatoire
                    </label>
                    {signataires.length > 1 && (
                      <button type="button" onClick={() => removeSig(i)} className="text-[#8B3A3A] text-xs hover:underline">Retirer</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none" data-testid="submit-request-signatures">
              {saving ? 'Envoi en cours...' : `Envoyer ${signataires.filter((s) => s.nom && s.email && s.telephone).length} invitation(s)`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
